import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, ArrowRight, Search, X, AlertTriangle, GitMerge } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = { name: "", phone: "", email: "", address: "", notes: "", tags: [] };
const TAG_PRESETS = ["VIP", "HNI", "Retail", "Corporate", "Prospect"];

const tagColor = (t) => {
  const map = {
    VIP: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    HNI: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    Retail: "bg-sky-500/15 text-sky-400 border-sky-500/30",
    Corporate: "bg-violet-500/15 text-violet-400 border-violet-500/30",
    Prospect: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return map[t] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [filterTag, setFilterTag] = useState("__all__");
  const [similar, setSimilar] = useState([]);
  const [showSimilar, setShowSimilar] = useState(false);
  const [confirmDup, setConfirmDup] = useState(null); // list of very-close matches awaiting confirmation
  const similarTimer = useRef(null);
  const [mergeSource, setMergeSource] = useState(null); // {id, name}
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeBusy, setMergeBusy] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/clients"), api.get("/clients-tags")]);
    setClients(c.data);
    setAllTags(t.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setTagInput(""); setEditId(null); setSimilar([]); setShowSimilar(false); setOpen(true); };
  const openEdit = (c) => {
    setForm({ name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", notes: c.notes || "", tags: c.tags || [] });
    setTagInput(""); setEditId(c.id); setSimilar([]); setShowSimilar(false); setOpen(true);
  };

  // Debounced similar-name lookup (only when creating a new client)
  const onNameChange = (v) => {
    setForm({ ...form, name: v });
    if (editId) return; // only during "Add New Client"
    if (similarTimer.current) clearTimeout(similarTimer.current);
    const q = v.trim();
    if (q.length < 2) { setSimilar([]); setShowSimilar(false); return; }
    similarTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/clients/similar`, { params: { name: q } });
        setSimilar(res.data || []);
        setShowSimilar((res.data || []).length > 0);
      } catch { /* silent */ }
    }, 250);
  };

  const addTag = (t) => {
    const v = t.trim();
    if (!v) return;
    if (form.tags.includes(v)) return;
    setForm({ ...form, tags: [...form.tags, v] });
    setTagInput("");
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  const doCreate = async () => {
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setOpen(false);
      setConfirmDup(null);
      setSimilar([]);
      setShowSimilar(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save client");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    // Only guard on new client creation
    if (!editId) {
      // Check for very-close matches: exact case-insensitive OR similarity >= 0.85
      const closeMatches = (similar || []).filter(s => s.exact || s.similarity >= 0.85);
      if (closeMatches.length > 0) {
        setConfirmDup(closeMatches);
        return;
      }
    }
    await doCreate();
  };

  const remove = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const openMerge = async (c) => {
    setMergeSource(c);
    setMergeTargetId("");
    setMergePreview(null);
    try {
      const r = await api.get(`/clients/${c.id}/merge-preview`);
      setMergePreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load merge info");
    }
  };

  const runMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    setMergeBusy(true);
    try {
      const r = await api.post(`/clients/merge`, { source_id: mergeSource.id, target_id: mergeTargetId });
      toast.success(`Merged into ${r.data.target_name}: ${r.data.moved_accounts} account(s), ${r.data.moved_transactions} trade(s) moved`);
      setMergeSource(null); setMergeTargetId(""); setMergePreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setMergeBusy(false);
    }
  };

  const filtered = useMemo(() => clients.filter(c => {
    if (filterTag !== "__all__" && !(c.tags || []).includes(filterTag)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return c.name.toLowerCase().includes(s);
  }), [clients, q, filterTag]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Directory</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">Clients</h1>
          <p className="text-slate-400 text-sm mt-2">Manage your client roster, tag key accounts, and access individual portfolios.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="btn-add-client-modal-open" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
              <UserPlus size={16} className="mr-2" /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E1524] border-slate-800 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Client" : "Add New Client"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
              <Field label="Full Name *">
                <div className="relative">
                  <Input
                    data-testid="form-client-name-input"
                    value={form.name}
                    onChange={e => onNameChange(e.target.value)}
                    onFocus={() => { if (!editId && similar.length > 0) setShowSimilar(true); }}
                    placeholder="e.g. Ravi Kumar"
                    autoComplete="off"
                  />
                  {!editId && showSimilar && similar.length > 0 && (
                    <div
                      data-testid="similar-clients-dropdown"
                      className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-slate-800 bg-[#0B1220] shadow-xl max-h-64 overflow-y-auto"
                    >
                      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
                        <span className="text-[11px] uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                          <AlertTriangle size={12} /> Similar clients found
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowSimilar(false)}
                          data-testid="similar-clients-dismiss"
                          className="text-slate-500 hover:text-slate-300"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <ul>
                        {similar.map(s => (
                          <li key={s.id} data-testid={`similar-client-${s.id}`}>
                            <Link
                              to={`/clients/${s.id}`}
                              className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-800/50"
                              onClick={() => setOpen(false)}
                            >
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-100 truncate">
                                  {s.name}
                                  {s.exact && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">EXACT MATCH</span>}
                                </div>
                                <div className="text-[11px] text-slate-500 truncate">
                                  {s.phone || "—"} · {s.email || "—"}
                                </div>
                              </div>
                              <span className="text-[10px] text-slate-500 shrink-0">
                                {Math.round((s.similarity || 0) * 100)}% match
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><Input data-testid="form-client-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91..." /></Field>
                <Field label="Email"><Input data-testid="form-client-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="client@example.com" /></Field>
              </div>
              <Field label="Address"><Textarea data-testid="form-client-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={2} /></Field>
              <Field label="Notes"><Textarea data-testid="form-client-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></Field>

              <Field label="Tags">
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="form-client-selected-tags">
                  {form.tags.map(t => (
                    <span key={t} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${tagColor(t)}`}>
                      {t}
                      <button type="button" onClick={() => removeTag(t)} data-testid={`btn-remove-tag-${t}`} className="hover:opacity-70"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                    placeholder="Type tag and press Enter"
                    data-testid="form-client-tag-input"
                  />
                  <Button type="button" onClick={() => addTag(tagInput)} variant="outline" className="border-slate-700 text-slate-800">Add</Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {TAG_PRESETS.filter(t => !form.tags.includes(t)).map(t => (
                    <button key={t} type="button" onClick={() => addTag(t)} data-testid={`btn-preset-tag-${t}`} className={`px-2 py-0.5 rounded-full text-xs border ${tagColor(t)} opacity-70 hover:opacity-100`}>+ {t}</button>
                  ))}
                </div>
              </Field>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
              <Button onClick={submit} data-testid="form-client-submit-button" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">{editId ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate-name confirmation dialog */}
        <AlertDialog open={!!confirmDup} onOpenChange={(o) => { if (!o) setConfirmDup(null); }}>
          <AlertDialogContent className="bg-[#0E1524] border-slate-800 text-white" data-testid="confirm-duplicate-client-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-300">
                <AlertTriangle size={18} /> Possible duplicate client
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                A client with a very similar name already exists. Please confirm you want to create a new record.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="rounded-lg border border-slate-800 bg-slate-900/50 divide-y divide-slate-800 max-h-56 overflow-y-auto">
              {(confirmDup || []).map(s => (
                <div key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100 truncate">
                      {s.name}
                      {s.exact && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">EXACT</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{s.phone || "—"} · {s.email || "—"}</div>
                  </div>
                  <span className="text-[10px] text-slate-500 shrink-0">{Math.round((s.similarity || 0) * 100)}%</span>
                </div>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-800 border-slate-700" data-testid="confirm-duplicate-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { setConfirmDup(null); doCreate(); }}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
                data-testid="confirm-duplicate-continue"
              >
                Create anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </header>

      <div className="card-tactical p-6">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name..." data-testid="input-client-search" className="pl-9 bg-slate-900/70 border-slate-800" />
          </div>
          <div className="w-44">
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger data-testid="select-filter-tag" className="bg-slate-900/70 border-slate-800">
                <SelectValue placeholder="Filter by tag" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                <SelectItem value="__all__" data-testid="filter-tag-option-all">All tags</SelectItem>
                {allTags.map(t => <SelectItem key={t} value={t} data-testid={`filter-tag-option-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="label-caps ml-auto">{filtered.length} clients</div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">
            No clients match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tactical" data-testid="table-clients-list">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tags</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th className="!text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}>
                    <td>
                      <Link to={`/clients/${c.id}`} className="font-semibold hover:text-emerald-400">{c.name}</Link>
                      {c.notes && <div className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">{c.notes}</div>}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map(t => (
                          <span key={t} className={`px-2 py-0.5 rounded-full text-[10px] border ${tagColor(t)}`}>{t}</span>
                        ))}
                        {(!c.tags || c.tags.length === 0) && <span className="text-slate-600 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="text-slate-300">{c.phone || "—"}</td>
                    <td className="text-slate-300">{c.email || "—"}</td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/clients/${c.id}`}><Button size="sm" variant="outline" data-testid={`btn-view-client-${c.id}`} className="border-slate-700 text-slate-700 hover:text-emerald-400"><ArrowRight size={14} /></Button></Link>
                        <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`btn-edit-client-${c.id}`} className="border-slate-700 text-slate-700"><Pencil size={14} /></Button>
                        <Button size="sm" variant="outline" onClick={() => openMerge(c)} data-testid={`btn-merge-client-${c.id}`} className="border-slate-700 text-slate-700 hover:text-amber-400" title="Merge into another client"><GitMerge size={14} /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`btn-delete-client-${c.id}`} className="border-slate-700 text-rose-400 hover:text-rose-300"><Trash2 size={14} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0E1524] border-slate-800 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete client?</AlertDialogTitle>
                              <AlertDialogDescription className="text-slate-400">This will also delete all their transaction history.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-slate-800 border-slate-700">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(c.id)} className="bg-rose-500 hover:bg-rose-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Merge clients dialog */}
      <Dialog open={!!mergeSource} onOpenChange={(o) => { if (!o) { setMergeSource(null); setMergeTargetId(""); setMergePreview(null); } }}>
        <DialogContent className="bg-[#0E1524] border-slate-800 text-white max-w-lg" data-testid="merge-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitMerge size={18} className="text-amber-400" /> Merge client
            </DialogTitle>
          </DialogHeader>
          {mergeSource && (
            <div className="space-y-4 mt-1">
              <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-sm">
                <div className="label-caps mb-1">Source (will be deleted)</div>
                <div className="font-semibold text-slate-100">{mergeSource.name}</div>
                {mergePreview && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    {mergePreview.accounts} CDS account(s) · {mergePreview.transactions} trade(s) will move
                  </div>
                )}
              </div>

              <Field label="Merge into (target client) *">
                <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                  <SelectTrigger data-testid="merge-target-select" className="bg-slate-900/70 border-slate-800">
                    <SelectValue placeholder="Select a client to keep" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0E1524] border-slate-800 text-white max-h-72">
                    {clients.filter(x => x.id !== mergeSource.id).map(x => (
                      <SelectItem key={x.id} value={x.id} data-testid={`merge-target-option-${x.id}`}>
                        {x.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="text-xs text-amber-400 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>All accounts and trades of <b>{mergeSource.name}</b> will be reassigned to the selected client. Tags will be unioned. This action is <b>irreversible</b>.</span>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setMergeSource(null); setMergeTargetId(""); }} className="border-slate-700 text-slate-800" data-testid="merge-cancel-btn">Cancel</Button>
            <Button
              onClick={runMerge}
              disabled={!mergeTargetId || mergeBusy}
              data-testid="merge-confirm-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold disabled:opacity-40"
            >
              {mergeBusy ? "Merging..." : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="label-caps !text-slate-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
